import {
	BaseState,
	closeModalTransactionAction,
	FormsActionTypes,
	FormsEntities,
	MessageBox,
	Mobile,
	ProjectHelper
} from '@bimser/common';
import { BCNotification, findMessage } from '@bimser/components';
import { ViewerHelpers } from '@bimser/components/dist/BCLayoutCreator';
import * as LayoutCreatorEntities from '@bimser/components/dist/BCLayoutCreator/common/entities';
import {
	createGuid,
	createSyncAction,
	FormItemTypes,
	IBaseAction,
	IDictionary,
	MLHelper,
	RxEventEmitter
} from '@bimser/core';
import { all, call, put, select, take, takeLeading } from 'redux-saga/effects';
import { ICheckRules } from '../entities/ICheckRules';
import { deepMergeFormItems, normalizeItemsForServer } from '../helper/deepMergeFormItems';
import { checkFormViewerItemRules } from './formSave';
import { checkFormItemRules } from './ruleValidation/rule';

export default function* watchFormSavedAs() {
	yield all([takeLeading(FormsActionTypes.FORM_SAVED_AS_SAGA, formSavedAs)]);
}

export function* formSavedAs(sagaAction: IBaseAction<FormsEntities.FormSaveAsAction>) {
	const mainState: BaseState = yield select();
	const payload = sagaAction.payload.payload;

	const stopProcess = yield call(checkFormItemRules, {
		formData: payload.items,
		selectedCulture: mainState.user.selectedCulture,
		defaultCulture: MLHelper.defaultLanguage?.culture,
		checkType: 'execution',
		windowId: sagaAction.payload.windowId,
		rules: payload.rules,
		validate: payload.validate
	} as ICheckRules);

	const viewersAndRepeaters = payload.items
		.filter(item =>
			[FormItemTypes.EControlTypes.FormViewer, FormItemTypes.EControlTypes.Repeater].includes(
				item.type as FormItemTypes.EControlTypes
			)
		)
		.values();

	let stopProcessViewerOrRepeater = false;

	for (const viewerOrRepeater of viewersAndRepeaters) {
		const result = yield call(checkFormViewerItemRules, viewerOrRepeater, payload.validate);
		if (!stopProcessViewerOrRepeater) {
			stopProcessViewerOrRepeater = result;
		}
	}

	if ((!stopProcess && !stopProcessViewerOrRepeater) || !payload.validate) {
		try {
			const response = yield call(SaveAsForm, payload.requestData);
			if (response) {
				const responseData: FormsEntities.IFormResponse = response.data;
				const items: LayoutCreatorEntities.INormalizedLayoutItems = responseData.form?.data; // New form data
				const documentId: number = responseData.documentId; // SavedAs document id
				const formId: string = sagaAction.payload.windowId; // Panel id
				const lastBatchGuid = createGuid();
				const currentFormData = mainState.forms?.get(sagaAction.payload.windowId);

				const mergedItems = deepMergeFormItems(
					currentFormData.items.toJS(),
					items,
					null,
					'Save'
				);
				const { structureItems, dataItems } =
					ViewerHelpers.splitDataAndStructureItems(mergedItems);

				let loadPayload = LayoutCreatorEntities.FormItemsLoadActionFactoryFromJS({
					items: mergedItems,
					dataItems:
						dataItems.toJS() as IDictionary<LayoutCreatorEntities.ILayoutDataItem>,
					structureItems: structureItems.toJS(),
					documentId,
					formId,
					lastBatchGuid,
					rules: null as any
				});

				loadPayload = loadPayload.set('rules', payload.rules);

				const updateSavedAsFormAction = createSyncAction(
					FormsActionTypes.FORM_ITEMS_LOADED_TRANSACTION,
					loadPayload
				);

				yield put(updateSavedAsFormAction);

				if (responseData.status === FormsEntities.IFormStatus.Completed) {
					RxEventEmitter.emit('external_' + sagaAction.payload.windowId, {
						type: 'FORM_SAVEDAs',
						windowId: sagaAction.payload.windowId,
						response: responseData
					});
					Mobile.MobileView.mobileServiceResponse(
						true,
						'SaveAs',
						JSON.stringify({
							status: 'Completed',
							formPanelId: sagaAction.payload.windowId
						})
					);
					yield put(closeModalTransactionAction(sagaAction.payload.windowId));
					BCNotification.success({
						message:
							payload.type === 'flow'
								? findMessage.get('102950')
								: findMessage.get('102951')
					});
				} else {
					RxEventEmitter.emit('external_' + sagaAction.payload.windowId, {
						type: 'FORM_SAVEDAs_FAILED',
						windowId: sagaAction.payload.windowId,
						response: null
					});
					Mobile.MobileView.mobileServiceResponse(
						false,
						'SaveAs',
						JSON.stringify({
							status: 'Failed',
							formPanelId: sagaAction.payload.windowId
						})
					);
					const message: any = {
						typeName: 'ShowMessageAction',
						dialogType: 'Toaster',
						alertType: 'Error',
						id: createGuid(),
						messages: findMessage.get?.('102597'),
						name: findMessage.get?.('100926'),
						enabled: true
					};
					Mobile.MobileView.mobileClientSideMessages([message]);
				}

				const action = yield take([FormsActionTypes.LAST_BATCH_GUID_RESOLVED]);
				if (action?.payload === lastBatchGuid) {
					payload.onAfterSaveAs?.(responseData.status, responseData.secretKey);
				}

				if (responseData.status === FormsEntities.IFormStatus.Canceled) {
					BCNotification.success({
						message: findMessage.get?.('100742'),
						description: findMessage.get?.('101549')
					}); // Form kaydetme işlemi iptal edildi
					Mobile.MobileView.mobileServiceResponse(
						false,
						'SaveAs',
						JSON.stringify({
							status: 'Canceled',
							formPanelId: sagaAction.payload.windowId
						})
					);
				}
			}
		} catch (error) {
			MessageBox.error({ error: error.exception.data });
			RxEventEmitter.emit('external_' + sagaAction.payload.windowId, {
				type: 'FORM_SAVED_AS_FAILED',
				windowId: sagaAction.payload.windowId,
				response: null
			});
			Mobile.MobileView.mobileServiceResponse(
				false,
				'SaveAs',
				JSON.stringify({ status: 'Failed', formPanelId: sagaAction.payload.windowId })
			);
		}
	} else {
		RxEventEmitter.emit('external_' + sagaAction.payload.windowId, {
			type: 'FORM_SAVED_FAILED',
			windowId: sagaAction.payload.windowId,
			response: null
		});
	}
}

export function SaveAsForm(data: FormsEntities.FormSaveRequestData) {
	const formData: LayoutCreatorEntities.INormalizedLayoutItems = removeFormRules(
		data.formData
	).toJS();
	return ProjectHelper.RunPromiseFormService({
		projectName: data.projectName,
		formName: data.formName,
		serviceName: 'SaveAs',
		requestParameters: {
			secretKey: data.secretKey,
			form: { data: normalizeItemsForServer(formData) }
		}
	});
}

function removeFormRules(
	data: LayoutCreatorEntities.NormalizedLayoutItems
): LayoutCreatorEntities.NormalizedLayoutItems {
	const formItem = data.entities.items.find(
		item => item.type === FormItemTypes.EContainerTypes.Form
	);
	return data.setIn(['entities', 'items', formItem?.id, 'properties', 'rules'], {});
}
